
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Lead } from "@/types";
import { generateVoiceMessage } from "./voiceService";
import { makeCall } from "./callService";
import { analyzeLeadForPersonalization } from "./analysisService";
import { simulateCampaignResults } from "./demoService";

export const startCampaign = async (leadIds: string[]): Promise<{ success: boolean; error?: string }> => {
  try {
    if (!leadIds.length) {
      return { success: false, error: "No leads selected" };
    }

    // Update the leads status to "in-progress"
    const { error } = await supabase
      .from("leads")
      .update({ status: "in-progress" })
      .in("id", leadIds);

    if (error) throw error;

    // For each lead, create a new call record
    const callPromises = leadIds.map(async (leadId) => {
      const { error: callError } = await supabase
        .from("calls")
        .insert([
          { 
            lead_id: leadId, 
            status: "queued" 
          }
        ]);
      
      if (callError) throw callError;
    });

    await Promise.all(callPromises);

    // In a real implementation, we would:
    // 1. Get the lead details (including phone number)
    // 2. Generate a voice message for each lead
    // 3. Make calls using Plivo
    
    // This would be done as a background process or via a queue
    // For now, we'll just return success

    return { success: true };
  } catch (error) {
    console.error("Error starting campaign:", error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : "An unknown error occurred" 
    };
  }
};

// New function to make real calls with the integrations
export const startRealCampaign = async (
  leads: Lead[],
  scriptTemplate: string,
  voiceId?: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    if (!leads.length) {
      return { success: false, error: "No leads provided" };
    }

    console.log(`Starting real campaign for ${leads.length} leads with script template: ${scriptTemplate.substring(0, 50)}...`);

    // Process each lead
    const callPromises = leads.map(async (lead) => {
      if (!lead.id || !lead.phone) {
        console.warn(`Skipping lead with missing data: ${lead.id}`);
        return;
      }

      // 1. First, analyze the lead to create a personalized approach
      console.log(`Analyzing lead for personalization: ${lead.name}`);
      const { success: analysisSuccess, personalizedScript, error: analysisError } = 
        await analyzeLeadForPersonalization(lead, scriptTemplate);
      
      if (!analysisSuccess) {
        console.error(`Error analyzing lead ${lead.id}: ${analysisError}`);
        // Continue with default script if analysis fails
      }

      // Determine which script to use
      const finalScript = personalizedScript || scriptTemplate;
      
      // 2. Generate voice message with ElevenLabs
      console.log(`Generating voice message for lead: ${lead.name}`);
      const { success: voiceSuccess, audioUrl, error: voiceError } = 
        await generateVoiceMessage(finalScript, voiceId);
      
      if (!voiceSuccess) {
        throw new Error(voiceError || "Failed to generate voice message");
      }

      // Update lead status
      const { error: updateError } = await supabase
        .from("leads")
        .update({ status: "in-progress" })
        .eq("id", lead.id);
      
      if (updateError) {
        console.error(`Error updating lead status: ${updateError.message}`);
        return;
      }

      // Create call record
      const { error: callError } = await supabase
        .from("calls")
        .insert([{ lead_id: lead.id, status: "queued" }]);
      
      if (callError) {
        console.error(`Error creating call record: ${callError.message}`);
        return;
      }

      // 3. Make the call
      console.log(`Making call to lead: ${lead.name} at ${lead.phone}`);
      const { success: callSuccess, error: callInitError } = await makeCall(
        lead.id,
        lead.phone,
        audioUrl as string
      );

      if (!callSuccess) {
        console.error(`Error initiating call to ${lead.phone}: ${callInitError}`);
      }
    });

    await Promise.all(callPromises);
    console.log("All calls have been initiated successfully");

    return { success: true };
  } catch (error) {
    console.error("Error starting real campaign:", error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : "An unknown error occurred" 
    };
  }
};

// Re-export everything from the individual service files
export { generateVoiceMessage } from "./voiceService";
export { makeCall } from "./callService";
export { generateIntentSummary, analyzeLeadForPersonalization } from "./analysisService";
export { simulateCampaignResults } from "./demoService";
